import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router";
import { AppShell } from "@/components/AppShell";
import { ChunkErrorBoundary, clearChunkReloadGuard } from "@/components/ChunkErrorBoundary";
import { RouteFallback } from "@/components/RouteFallback";
import { SidebarProvider } from "@/context/SidebarContext";

// Route-level code splitting: each page is emitted as its own async chunk so
// first paint no longer downloads + parses the entire app (chart-heavy pages
// like Traffic/Revenue and their deps load on demand). Pages are named exports,
// hence the `.then(default)` wrapper.
//
// Suspense is two-tier: this top-level boundary covers the initial load and the
// /login route (outside AppShell); a SECOND <Suspense> inside AppShell wraps
// <Outlet/> so that, once the shell is mounted, only the content area shows the
// fallback and the sidebar stays put (the nearer boundary catches the
// suspension). ChunkErrorBoundary turns a failed chunk fetch — e.g. a stale
// client navigating after a deploy — into a reload instead of a blank screen.
const Login = lazy(() => import("@/pages/Login").then((m) => ({ default: m.Login })));
const Cockpit = lazy(() => import("@/pages/Cockpit").then((m) => ({ default: m.Cockpit })));
const Agents = lazy(() => import("@/pages/Agents").then((m) => ({ default: m.Agents })));
const AgentDetail = lazy(() =>
  import("@/pages/AgentDetail").then((m) => ({ default: m.AgentDetail })),
);
const RunDetail = lazy(() => import("@/pages/RunDetail").then((m) => ({ default: m.RunDetail })));
const Approvals = lazy(() => import("@/pages/Approvals").then((m) => ({ default: m.Approvals })));
const ApprovalDetail = lazy(() =>
  import("@/pages/ApprovalDetail").then((m) => ({ default: m.ApprovalDetail })),
);
const Goals = lazy(() => import("@/pages/Goals").then((m) => ({ default: m.Goals })));
const Revenue = lazy(() => import("@/pages/Revenue").then((m) => ({ default: m.Revenue })));
const Traffic = lazy(() => import("@/pages/Traffic").then((m) => ({ default: m.Traffic })));
const Products = lazy(() => import("@/pages/Products").then((m) => ({ default: m.Products })));
const Customers = lazy(() => import("@/pages/Customers").then((m) => ({ default: m.Customers })));
const Settings = lazy(() => import("@/pages/Settings").then((m) => ({ default: m.Settings })));

export function App() {
  // Reaching here means the core bundle loaded; reset the chunk-reload guard so
  // a future stale-deploy chunk failure is allowed its one recovery reload.
  useEffect(() => clearChunkReloadGuard(), []);

  return (
    <SidebarProvider>
      <ChunkErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<AppShell />}>
              <Route index element={<Cockpit />} />
              <Route path="agents" element={<Agents />} />
              <Route path="agents/:agentId" element={<AgentDetail />} />
              <Route path="agents/:agentId/runs/:runId" element={<RunDetail />} />
              <Route path="approvals" element={<Approvals />} />
              <Route path="approvals/:actionId" element={<ApprovalDetail />} />
              <Route path="goals" element={<Goals />} />
              <Route path="store/traffic" element={<Traffic />} />
              <Route path="store/revenue" element={<Revenue />} />
              <Route path="store/orders" element={<Navigate to="/store/revenue" replace />} />
              <Route path="store/products" element={<Products />} />
              <Route path="store/customers" element={<Customers />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ChunkErrorBoundary>
    </SidebarProvider>
  );
}
